import { useForm } from '@tanstack/react-form'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { toast } from 'sonner'
import { EntityLinker } from './entity-linker'

import type { Task } from '@/gen/cortado/v1/actions_pb'
import { CreateTaskRequestSchema, UpdateTaskRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { TaskStatusEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useCreateTask, useUpdateTask } from '@/stores/tasks.store'
import { useOrganizationUsers } from '@/stores/organization.store'
import { useSystemSettings } from '@/stores/settings.store'
import { dateToDateOnlyTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { formatEnumName } from '@/lib/enum-utils'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { DatePicker } from '@/components/shared/date-picker'


interface TaskSheetProps {
  organizationId: string
  parentType?: ParentTypeEnum
  parentId?: string
  task?: Task // If provided, this is edit mode; otherwise create mode
  /** Create-mode prefill (e.g. from an inline composer). Ignored in edit mode. */
  initialValues?: { title?: string; description?: string; assignedTo?: string; dueDate?: Date }
  /** When set, the linked entity is fixed: shown read-only under this label instead of the picker. */
  lockedParentLabel?: string
  /** Called after a successful create or update, before the sheet closes. */
  onSaved?: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

/**
 * TaskSheet - Reusable sheet component for creating and updating tasks
 *
 * Features:
 * - Dual mode: Create new tasks or edit existing ones
 * - Form pre-filled when editing (task prop provided)
 * - Validates required fields
 * - Uses TanStack Form for form state management
 * - Leverages useCreateTask/useUpdateTask hooks for optimized cache updates
 * - Shows loading states during mutation
 * - Shows toast notifications on success/error
 * - Resets form on close
 *
 * @param organizationId - Organization ID for the task
 * @param parentType - Type of parent entity (inquiry, contact, booking, event)
 * @param parentId - ID of the parent entity
 * @param task - Optional task to edit (if omitted, creates new task)
 * @param initialValues - Optional create-mode prefill
 * @param lockedParentLabel - Optional label that locks the linked entity
 * @param onSaved - Optional callback after a successful save
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function TaskSheet({
  organizationId,
  parentType,
  parentId,
  task,
  initialValues,
  lockedParentLabel,
  onSaved,
  open,
  onOpenChange,
  layout,
}: TaskSheetProps) {
  const isEditMode = !!task
  const { mutation: createMutation } = useCreateTask()
  const { mutation: updateMutation } = useUpdateTask()
  const { users, isLoading: usersLoading } = useOrganizationUsers()
  const { settings } = useSystemSettings()

  // Compute initial parent value for EntityLinker
  const initialParentType = isEditMode
    ? (task?.parentType ?? parentType)
    : parentType
  const initialParentId = isEditMode
    ? (task?.parentId ?? parentId)
    : parentId

  const isLinkableParent = initialParentType !== undefined
    && initialParentType !== ParentTypeEnum.PARENT_TYPE_ORGANIZATION
    && initialParentType !== ParentTypeEnum.PARENT_TYPE_UNKNOWN
    && initialParentId !== undefined

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      title: task?.title ?? initialValues?.title ?? '',
      description: task?.description ?? initialValues?.description ?? '',
      assignedTo: task?.assignedTo ?? initialValues?.assignedTo ?? '',
      dueDate: task?.dueDate ? timestampToDateOnly(task.dueDate) : initialValues?.dueDate as Date | undefined,
      taskStatus: task?.taskStatus ?? TaskStatusEnum.TASK_STATUS_NOT_STARTED,
      linkedEntity: isLinkableParent
        ? { parentType: initialParentType!, parentId: initialParentId! }
        : null as { parentType: ParentTypeEnum; parentId: string } | null,
    },
    onSubmit: async ({ value }) => {
      try {
        if (!value.title.trim()) {
          toast.error('Title is required')
          return
        }
        if (!value.assignedTo) {
          toast.error('Please assign the task to someone')
          return
        }
        if (!value.dueDate) {
          toast.error('Due date is required')
          return
        }

        const resolvedParentType = value.linkedEntity?.parentType ?? ParentTypeEnum.PARENT_TYPE_ORGANIZATION
        const resolvedParentId = value.linkedEntity?.parentId ?? organizationId

        if (isEditMode && task) {
          const request = create(UpdateTaskRequestSchema, {
            id: task.id,
            title: value.title.trim(),
            description: value.description.trim(),
            assignedTo: value.assignedTo,
            dueDate: dateToDateOnlyTimestamp(value.dueDate),
            taskStatus: value.taskStatus,
            parentType: resolvedParentType,
            parentId: resolvedParentId,
            updateMask: create(FieldMaskSchema, {
              paths: ['title', 'description', 'assigned_to', 'due_date', 'task_status', 'parent_type', 'parent_id'],
            }),
          })
          await updateMutation.mutateAsync(request)
          toast.success('Task updated successfully')
        } else {
          const request = create(CreateTaskRequestSchema, {
            organizationId,
            parentType: resolvedParentType,
            parentId: resolvedParentId,
            title: value.title.trim(),
            description: value.description.trim(),
            assignedTo: value.assignedTo,
            dueDate: dateToDateOnlyTimestamp(value.dueDate),
          })
          await createMutation.mutateAsync(request)
          toast.success('Task created successfully')
        }

        onSaved?.()
        onOpenChange(false)
        form.reset()
      } catch (error) {
        console.error('Failed to save task:', error)
        reportError(error, { feature: 'task_save', kind: 'mutation' })
        toast.error(`Failed to ${isEditMode ? 'update' : 'create'} task`, {
          description: getErrorDescription(error),
        })
      }
    },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  // Get all TaskStatus values from system settings (excluding UNKNOWN)
  const taskStatusOptions = (settings?.taskStatuses || [])
    .filter(status => status !== TaskStatusEnum.TASK_STATUS_UNKNOWN)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn(layout === 'pipeline' && 'w-full sm:max-w-xl')}>
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Task' : 'New Task'}</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? 'Update the task details. Click save when you\'re done.'
              : 'Create a new task. Click save when you\'re done.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="task-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          <form.Field name="title">
            {(field) => (
              <div className="grid">
                <Label htmlFor="title" className="mb-3">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isPending}
                  placeholder="Enter task title"
                  required
                />
              </div>
            )}
          </form.Field>

          {lockedParentLabel ? (
            <div className="grid gap-3">
              <Label>Linked to</Label>
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">{lockedParentLabel}</div>
            </div>
          ) : (
            <form.Field name="linkedEntity">
              {(field) => (
                <EntityLinker
                  value={field.state.value}
                  onChange={(val) => field.handleChange(val)}
                  disabled={isPending}
                />
              )}
            </form.Field>
          )}

          <form.Field name="description">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={isPending}
                  placeholder="Enter task description"
                  rows={4}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="assignedTo">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="assigned-to">
                  Assigned To <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  disabled={isPending || usersLoading}
                >
                  <SelectTrigger id="assigned-to" className="w-full">
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.user?.id} value={user.user?.id ?? ''}>
                        {user.user?.firstName} {user.user?.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="dueDate">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="due-date">
                  Due Date <span className="text-destructive">*</span>
                </Label>
                <DatePicker
                  value={field.state.value}
                  onChange={(date) => field.handleChange(date)}
                  disabled={isPending}
                />
              </div>
            )}
          </form.Field>

          {isEditMode && (
            <form.Field name="taskStatus">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="task-status">Status</Label>
                  <Select
                    value={String(field.state.value)}
                    onValueChange={(value) => field.handleChange(Number(value))}
                    disabled={isPending}
                  >
                    <SelectTrigger id="task-status" className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {taskStatusOptions.map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {formatEnumName(value, TaskStatusEnum)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          </form>
        </SheetBody>
        <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe selector={(s) => [s.values, s.isSubmitting] as const}>
            {([values, isSubmitting]) => {
              const disabled =
                isPending ||
                isSubmitting ||
                !values.title.trim() ||
                !values.assignedTo ||
                !values.dueDate
              return (
                <Button type="submit" form="task-form" disabled={disabled}>
                  {isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              )
            }}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
